import { useCallback, useEffect, useState } from "react";
import { flushOfflineQueues, getLastSyncInfo, isBrowserOnline } from "../utils/offlineSync";

export default function useOfflineSync() {
  const [online, setOnline] = useState(() => isBrowserOnline());
  const [pendingCount, setPendingCount] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [lastFlushAt, setLastFlushAt] = useState(null);

  const refreshMeta = useCallback(async () => {
    const info = await getLastSyncInfo();
    setPendingCount(info.pendingCount);
    setLastFlushAt(info.lastFlush?.at || null);
  }, []);

  const runFlush = useCallback(async () => {
    if (!isBrowserOnline()) return null;
    setSyncing(true);
    try {
      const result = await flushOfflineQueues();
      await refreshMeta();
      if (result.flushed > 0 && typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("6amico-offline-sync", { detail: result })
        );
      }
      return result;
    } finally {
      setSyncing(false);
    }
  }, [refreshMeta]);

  useEffect(() => {
    refreshMeta();
    function onOnline() {
      setOnline(true);
      runFlush();
    }
    function onOffline() {
      setOnline(false);
      refreshMeta();
    }
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, [refreshMeta, runFlush]);

  return {
    online,
    pendingCount,
    syncing,
    lastFlushAt,
    refreshMeta,
    runFlush,
    showOfflineBadge: !online || pendingCount > 0,
  };
}
